/**
 * Conversión v1 ↔ v2 para que el editor (motor v1) pueda persistir/cargar
 * defs del catálogo v2.
 *
 * v1 es subconjunto de v2: spawn rect, velocity rect, alpha 2-stop, scale
 * 2-stop, tint flat. Lo que el motor v1 no soporta (shape circle/ring,
 * velocity radial/directional, curvas multi-stop, sub-emitters, trail,
 * noise) se preserva en el round-trip pero NO se ve en preview.
 */

import type { ParticleEffectDef } from '../../systems/ParticleSystem';
import type {
  ParticleEffectDefV2,
  EmissionModule,
  PhysicsModule,
  OrbitModule,
  RotationModule,
  NumberCurveModule,
  NumberCurve,
  VelocityModule,
  SpawnShapeModule,
  ParticleBlendMode,
} from '@shared/types/particles';

// ────────────────────────────────────────────────────────────────────────────
// v1 → v2
// ────────────────────────────────────────────────────────────────────────────

export function v1ToV2(v1: ParticleEffectDef, id: string): ParticleEffectDefV2 {
  const emission: EmissionModule = {
    rate: v1.emitRate,
    maxAlive: v1.numParticles,
    life: [...v1.life],
  };
  if (v1.emitRate <= 0) {
    emission.burstCount = v1.numParticles;
  }

  const shape: SpawnShapeModule = {
    type: 'rect',
    w: v1.spawnX[1] - v1.spawnX[0],
    h: v1.spawnY[1] - v1.spawnY[0],
  };

  const velocity: VelocityModule = {
    mode: 'random_rect',
    vx: [...v1.speedX],
    vy: [...v1.speedY],
  };

  const physics: PhysicsModule = {};
  if (v1.gravityY !== 0) physics.gravityY = v1.gravityY;
  if (v1.friction !== 0) physics.drag = v1.friction;
  if (v1.bounce !== 0) physics.bounce = v1.bounce;

  const orbit: OrbitModule | undefined = v1.orbit.enabled
    ? {
        radiusX: v1.orbit.radiusX,
        radiusY: v1.orbit.radiusY,
        speed: v1.orbit.speed,
        ...(v1.orbit.radiusStart !== undefined && { radiusStart: v1.orbit.radiusStart }),
      }
    : undefined;

  const rotation: RotationModule | undefined =
    v1.spin[0] !== 0 || v1.spin[1] !== 0 ? { spin: [...v1.spin] } : undefined;

  const alphaOverLife: NumberCurveModule = {
    stops: [
      [0, v1.alpha[0]],
      [1, v1.alpha[1]],
    ],
  };

  const sizeOverLife: NumberCurveModule = {
    stops: [
      [0, v1.scale[0]],
      [1, v1.scale[1]],
    ],
  };

  return {
    id,
    version: 2,
    nameEs: v1.name,
    spriteKey: v1.spriteKey,
    frames: [...v1.frames],
    frameScales: { ...v1.frameScales },
    emission,
    shape,
    velocity,
    physics: Object.keys(physics).length > 0 ? physics : undefined,
    orbit,
    rotation,
    alphaOverLife,
    sizeOverLife,
    blendMode: v1.blendMode as ParticleBlendMode,
    tint: [...v1.tint],
    loop: v1.loop,
    duration: v1.duration,
    behindEntity: v1.behindEntity ?? false,
    depthMode: v1.depthMode ?? 'fixed',
    screenShake: v1.screenShake,
    screenFlash: v1.screenFlash,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// v2 → v1 (best-effort, pierde módulos no soportados)
// ────────────────────────────────────────────────────────────────────────────

export function v2ToV1(v2: ParticleEffectDefV2): ParticleEffectDef {
  // Spawn area desde shape (solo rect/point son mapeables directo).
  let spawnX: [number, number] = [-5, 5];
  let spawnY: [number, number] = [-5, 5];
  if (v2.shape?.type === 'rect') {
    spawnX = [-v2.shape.w / 2, v2.shape.w / 2];
    spawnY = [-v2.shape.h / 2, v2.shape.h / 2];
  } else if (v2.shape?.type === 'point') {
    spawnX = [0, 0];
    spawnY = [0, 0];
  } else if (v2.shape?.type === 'circle') {
    spawnX = [-v2.shape.radius, v2.shape.radius];
    spawnY = [-v2.shape.radius, v2.shape.radius];
  }

  // Velocity rect-style. Otros modos se aproximan.
  let speedX: [number, number] = [-10, 10];
  let speedY: [number, number] = [-30, -60];
  if (v2.velocity?.mode === 'random_rect') {
    speedX = [...v2.velocity.vx];
    speedY = [...v2.velocity.vy];
  } else if (v2.velocity?.mode === 'directional') {
    const angle = (v2.velocity.angle[0] + v2.velocity.angle[1]) / 2;
    const speed = (v2.velocity.speed[0] + v2.velocity.speed[1]) / 2;
    const rad = (angle * Math.PI) / 180;
    const vx = Math.cos(rad) * speed;
    const vy = Math.sin(rad) * speed;
    speedX = [vx - 10, vx + 10];
    speedY = [vy - 10, vy + 10];
  } else if (v2.velocity?.mode === 'radial') {
    const s = v2.velocity.speed;
    speedX = [-s[1], s[1]];
    speedY = [-s[1], s[1]];
  }

  // Curvas → 2-stop heurística pico-aware: el `start` toma el VALOR PICO
  // (max) de la curva para no perder fade-in→peak→fade-out típico de auras
  // (`[[0,0],[0.3,1],[1,0]]` → `[1, 0]` en vez del bug previo `[0, 0]`).
  // Para curvas decrecientes monótonas (`[[0,1],[1,0]]`) el pico es 1
  // que coincide con el primer stop → comportamiento sin cambios.
  const peakStop = (stops: NumberCurve, fallbackStart: number): number => {
    if (!stops || stops.length === 0) return fallbackStart;
    return stops.reduce((max, [, v]) => (v > max ? v : max), -Infinity);
  };
  const lastStop = (stops: NumberCurve, fallbackEnd: number): number => {
    if (!stops || stops.length === 0) return fallbackEnd;
    return stops[stops.length - 1]?.[1] ?? fallbackEnd;
  };

  const alpha: [number, number] = v2.alphaOverLife?.stops
    ? [peakStop(v2.alphaOverLife.stops, 1), lastStop(v2.alphaOverLife.stops, 0)]
    : [1, 0];

  const scale: [number, number] = v2.sizeOverLife?.stops
    ? [peakStop(v2.sizeOverLife.stops, 1), lastStop(v2.sizeOverLife.stops, 0.5)]
    : [1, 0.5];

  return {
    name: v2.nameEs ?? v2.id,
    spriteKey: v2.spriteKey,
    frames: [...v2.frames],
    numParticles: v2.emission.maxAlive,
    emitRate: v2.emission.rate,
    spawnX,
    spawnY,
    speedX,
    speedY,
    life: [...v2.emission.life],
    gravityY: v2.physics?.gravityY ?? 0,
    friction: v2.physics?.drag ?? 0,
    bounce: v2.physics?.bounce ?? 0,
    spin: v2.rotation?.spin ? [...v2.rotation.spin] : [0, 0],
    orbit: v2.orbit
      ? {
          enabled: true,
          radiusX: v2.orbit.radiusX,
          radiusY: v2.orbit.radiusY,
          speed: v2.orbit.speed,
          radiusStart: v2.orbit.radiusStart,
        }
      : { enabled: false, radiusX: 30, radiusY: 15, speed: 180 },
    alpha,
    scale,
    frameScales: { ...(v2.frameScales ?? {}) },
    blendMode: (v2.blendMode === 'MULTIPLY' ? 'NORMAL' : v2.blendMode) as ParticleEffectDef['blendMode'],
    tint: v2.tint && v2.tint.length > 0 ? [...v2.tint] : [0xffffff],
    loop: v2.loop,
    duration: v2.duration,
    behindEntity: v2.behindEntity ?? false,
    depthMode: v2.depthMode ?? 'fixed',
    screenShake: v2.screenShake,
    screenFlash: v2.screenFlash,
  };
}
